import React, { useRef, useEffect, useState } from "react";
import { nettoyerHtml, htmlVersTexte, COULEURS_TEXTE, COULEURS_SURLIGNAGE } from "../utils/texteRiche";

/**
 * UNE BARRE DE MISE EN FORME POUR LE CONTENU DES COURS.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * CE QU'ELLE REMPLACE. Les sections d'un bloc « Texte / Cours » se saisissaient dans un
 * `<textarea>` : le coach ne pouvait ni mettre un mot en gras, ni souligner une règle de
 * grammaire, ni faire une liste. Tout arrivait chez l'apprenant en gris uniforme.
 *
 * POURQUOI PAS UNE BIBLIOTHÈQUE. Quill ou TipTap pèsent plus de 100 Ko et produisent leur
 * propre HTML, qu'il faudrait ensuite apprendre à afficher — et à nettoyer. Ici on n'ÉMET que
 * les balises qu'on sait afficher (voir `utils/texteRiche`), et la liste est la même des deux
 * côtés. Ce qui n'est pas dans la liste ne peut ni être écrit, ni être rendu.
 *
 * `execCommand` EST DÉPRÉCIÉ, ET C'EST ASSUMÉ. Aucun navigateur ne l'a retiré, tous
 * l'implémentent, et son remplaçant n'existe pas. L'alternative serait de manipuler les
 * sélections à la main — beaucoup de code, pour le même résultat.
 *
 * LE CHAMP N'EST PAS CONTRÔLÉ PAR REACT, et c'est le point délicat. Réécrire `innerHTML` à
 * chaque frappe replacerait le curseur au début à chaque lettre. On pose donc le contenu UNE
 * fois au montage, puis on laisse le navigateur gérer la frappe en remontant les changements
 * au parent. `dernierEmis` garde ce qu'on vient de remonter : si le parent nous le renvoie
 * tel quel, on n'y touche pas.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 *
 * @param {string}   valeur       le HTML courant
 * @param {function} onChange     reçoit ({ html, texte }) à chaque modification
 * @param {string}   placeholder  texte affiché quand le champ est vide
 */

const OUTILS = [
  { cmd: "bold",            icone: "B",  titre: "Gras (Ctrl+B)",       style: { fontWeight: 800 } },
  { cmd: "italic",          icone: "I",  titre: "Italique (Ctrl+I)",   style: { fontStyle: "italic", fontFamily: "Georgia, serif" } },
  { cmd: "underline",       icone: "U",  titre: "Souligné (Ctrl+U)",   style: { textDecoration: "underline" } },
  { cmd: "strikeThrough",   icone: "S",  titre: "Barré",               style: { textDecoration: "line-through" } },
  { sep: true },
  { cmd: "subscript",   icone: "x₂", titre: "Indice" },
  { cmd: "superscript", icone: "x²", titre: "Exposant" },
  { sep: true },
  { cmd: "insertUnorderedList", icone: "•—", titre: "Liste à puces" },
  { cmd: "insertOrderedList",   icone: "1.", titre: "Liste numérotée" },
  { sep: true },
  { cmd: "justifyLeft",   icone: "⬅", titre: "Aligner à gauche" },
  { cmd: "justifyCenter", icone: "⬌", titre: "Centrer" },
  { cmd: "justifyRight",  icone: "➡", titre: "Aligner à droite" },
  { sep: true },
  { cmd: "formatBlock", arg: "<h3>", icone: "T", titre: "Sous-titre", style: { fontWeight: 800, fontSize: 13 } },
  { cmd: "formatBlock", arg: "<blockquote>", icone: "❝", titre: "Exemple / citation" },
  { cmd: "formatBlock", arg: "<p>",  icone: "¶", titre: "Texte normal" },
  { sep: true },
  { cmd: "removeFormat", icone: "✕", titre: "Effacer la mise en forme" },
];

export default function EditeurTexteRiche({ valeur, onChange, placeholder = "Rédigez le contenu de cette section…", hauteurMin = 110 }) {
  const zoneRef = useRef(null);
  const [menuOuvert, setMenuOuvert] = useState(null);
  const dernierEmis = useRef(null);

  /* On ne réécrit le champ QUE lorsque la valeur reçue ne vient pas de nous : sinon le
     curseur repartirait au début à chaque caractère tapé. */
  useEffect(() => {
    const el = zoneRef.current;
    if (!el) return;
    const recu = valeur || "";
    if (recu === dernierEmis.current) return;
    if (recu !== el.innerHTML) el.innerHTML = nettoyerHtml(recu);
  }, [valeur]);

  const remonter = () => {
    const el = zoneRef.current;
    if (!el) return;
    const html = nettoyerHtml(el.innerHTML);
    dernierEmis.current = html;
    /* On remonte AUSSI le texte nu : il reste la seule version lisible par ce qui ne sait
       pas afficher du HTML — exports, recherche, écrans plus anciens. */
    onChange({ html, texte: htmlVersTexte(html) });
  };

  const appliquer = (outil) => {
    const el = zoneRef.current;
    if (!el) return;
    el.focus();
    /* `styleWithCSS` POUR LES SEULES COULEURS, et c'est une leçon payée : activé pour
       TOUTES les commandes, il fait écrire au navigateur `<span style="font-weight:bold">`
       au lieu de `<b>` — un style absent de la liste autorisée, donc retiré au nettoyage.
       Gras, italique, souligné et barré disparaissaient alors purement et simplement.

       Pour la couleur il reste indispensable : sans lui, Chrome et Safari écrivent
       `<font color="…">`. On l'active donc là, et seulement là. */
    const commandeCouleur = outil.cmd === "foreColor" || outil.cmd === "hiliteColor";
    try {
      document.execCommand("styleWithCSS", false, commandeCouleur);
    } catch { /* commande inconnue : la conversion de `<font>` prend le relais */ }
    try {
      document.execCommand(outil.cmd, false, outil.arg || null);
    } catch { /* un navigateur qui refuse : le texte reste saisissable, sans mise en forme */ }
    remonter();
  };

  /* LE COLLAGE PASSE EN TEXTE BRUT. Un copier-coller depuis Word ou une page web amène des
     dizaines d'attributs, des polices en dur et des tailles fixes : la section ne
     ressemblerait plus au reste du cours. Le nettoyage les retirerait de toute façon — autant
     ne jamais les laisser entrer. */
  const collerSansMiseEnForme = (e) => {
    e.preventDefault();
    const texte = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, texte);
    remonter();
  };

  const vide = !htmlVersTexte(valeur || "");

  return (
    <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "5px 6px",
        background: "#f8fafc", borderBottom: "1px solid #e5e7eb", flexWrap: "wrap" }}>
        {OUTILS.map((o, i) => o.sep ? (
          <span key={`s${i}`} style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
        ) : (
          <button key={`${o.cmd}${o.arg || ""}`} type="button" title={o.titre}
            /* `onMouseDown` et non `onClick` : un clic retire d'abord le focus du champ, donc
               la sélection, et la commande ne s'appliquerait à rien. */
            onMouseDown={(e) => { e.preventDefault(); appliquer(o); }}
            style={{ minWidth: 28, height: 26, border: "none", background: "transparent",
              borderRadius: 5, cursor: "pointer", fontSize: 12.5, color: "#374151",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "inherit", padding: "0 6px", ...o.style }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#e2e8f0"}
            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
            {o.icone}
          </button>
        ))}

        <span style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />

        {/* ── COULEUR ET SURLIGNAGE ──────────────────────────────────────────────────
            Une PALETTE FERMÉE, pas un sélecteur libre. `nettoyerHtml` ne laisse passer
            que ces valeurs exactes : proposer une couleur qu'il refuserait donnerait un
            bouton qui ne fait visiblement rien. Les deux listes sont la même source. */}
        {[
          { id: "texte",     icone: "A", titre: "Couleur du texte", cmd: "foreColor", palette: COULEURS_TEXTE, souligne: true },
          { id: "surlignage", icone: "🖍", titre: "Surlignage",      cmd: "hiliteColor", palette: COULEURS_SURLIGNAGE },
        ].map(menu => (
          <span key={menu.id} style={{ position: "relative" }}>
            <button type="button" title={menu.titre}
              onMouseDown={(e) => { e.preventDefault(); setMenuOuvert(m => m === menu.id ? null : menu.id); }}
              style={{ minWidth: 28, height: 26, border: "none", background: menuOuvert === menu.id ? "#e2e8f0" : "transparent",
                borderRadius: 5, cursor: "pointer", fontSize: 12.5, color: "#374151",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "inherit", padding: "0 6px", fontWeight: 700,
                borderBottom: menu.souligne ? "3px solid #dc2626" : "none" }}>
              {menu.icone}
            </button>
            {menuOuvert === menu.id && (
              <div style={{ position: "absolute", top: 30, left: 0, zIndex: 40, background: "#fff",
                border: "1px solid #e5e7eb", borderRadius: 8, padding: 6, display: "flex", gap: 4,
                boxShadow: "0 6px 18px rgba(0,0,0,.12)" }}>
                {menu.palette.map(c => (
                  <button key={c.valeur} type="button" title={c.nom}
                    onMouseDown={(e) => { e.preventDefault(); appliquer({ cmd: menu.cmd, arg: c.valeur }); setMenuOuvert(null); }}
                    style={{ width: 22, height: 22, borderRadius: 5, cursor: "pointer",
                      border: "1px solid rgba(0,0,0,.15)", background: c.valeur }} />
                ))}
              </div>
            )}
          </span>
        ))}
      </div>

      <div style={{ position: "relative" }}>
        {vide && (
          <div style={{ position: "absolute", top: 10, left: 12, fontSize: 13, color: "#9ca3af", pointerEvents: "none" }}>
            {placeholder}
          </div>
        )}
        <div
          ref={zoneRef}
          contentEditable
          suppressContentEditableWarning
          onInput={remonter}
          onBlur={remonter}
          onPaste={collerSansMiseEnForme}
          style={{ minHeight: hauteurMin, padding: "10px 12px", fontSize: 13, lineHeight: 1.7,
            color: "#374151", outline: "none", fontFamily: "inherit" }}
        />
      </div>
    </div>
  );
}
